import type { ReactNode } from 'react';
import type { AnalyticsAudience, ResolvedAnalyticsFilters } from '@nest-recruit/shared';
import { analyticsUrlOf, type AnalyticsUrl } from '../api/analytics-api';
import type { ReportUrl } from '../api/reports-api';
import { Filter, ReportFilters } from './ReportFilters';
import { useSourceOptions } from '../../../lib/app-settings';

const ALL = '';

/**
 * F17.03 for S29: the command center's bar plus role family (§L27 RPT-03a) and source
 * (RPT-05a). Only the HR Lead chooses a recruiter: a recruiter's reports are their own, and a
 * hiring manager's are their departments'.
 */
export function AnalyticsFilters({
  filters,
  audience,
  onChange,
  roleFamilies,
  hasSource = false,
}: {
  readonly filters: ResolvedAnalyticsFilters;
  readonly audience: AnalyticsAudience;
  readonly onChange: (next: AnalyticsUrl) => void;
  /** Offered when the report can narrow by role family. */
  readonly roleFamilies?: readonly string[];
  readonly hasSource?: boolean;
}): ReactNode {
  const current = analyticsUrlOf(filters);
  const sourceOptions = useSourceOptions('filter');
  const extras = (url: AnalyticsUrl): AnalyticsUrl => ({
    ...(url.roleFamily ? { roleFamily: url.roleFamily } : {}),
    ...(url.jobId ? { jobId: url.jobId } : {}),
    ...(url.source ? { source: url.source } : {}),
  });
  const keep = (next: ReportUrl): void => onChange({ ...next, ...extras(current) });
  const setExtra = (patch: AnalyticsUrl): void => {
    const merged = { ...current, ...patch };
    onChange({ ...withoutExtras(merged), ...extras({ roleFamily: merged.roleFamily || undefined, jobId: merged.jobId || undefined, source: merged.source || undefined }) });
  };
  const isExtraActive = filters.roleFamily !== null || filters.source !== null || filters.jobId !== null;

  return (
    <ReportFilters
      filters={filters}
      onChange={keep}
      isRecruiterHidden={audience !== 'all'}
      isExtraActive={isExtraActive}
      onClear={() => onChange({ period: filters.period, ...(filters.period === 'custom' ? { from: filters.from, to: filters.to } : {}) })}
      extra={
        <>
          {roleFamilies && (
            <Filter
              label="Role family"
              value={filters.roleFamily ?? ALL}
              options={[{ value: ALL, label: 'All role families' }, ...roleFamilies.map((family) => ({ value: family, label: family }))]}
              onChange={(roleFamily) => setExtra({ roleFamily })}
            />
          )}
          {hasSource && (
            <Filter
              label="Source"
              value={filters.source ?? ALL}
              options={[{ value: ALL, label: 'All sources' }, ...sourceOptions]}
              onChange={(source) => setExtra({ source })}
            />
          )}
        </>
      }
    />
  );
}

function withoutExtras(url: AnalyticsUrl): ReportUrl {
  return {
    ...(url.period ? { period: url.period } : {}),
    ...(url.from ? { from: url.from } : {}),
    ...(url.to ? { to: url.to } : {}),
    ...(url.ventureId ? { ventureId: url.ventureId } : {}),
    ...(url.departmentId ? { departmentId: url.departmentId } : {}),
    ...(url.recruiterUserId ? { recruiterUserId: url.recruiterUserId } : {}),
  };
}
